/**
 * The welcome promo: what a visitor earns by giving the site their email.
 *
 * Two offers run as an A/B test, each named by its code: 40% off the first
 * kit, or the first kit free. A visitor is in one test group, which decides
 * the code they are offered, and the promo is active once they have signed
 * up, followed a link that carries the code, or reached a landing page that
 * applies it. No code ever reaches Shopify: the checkout sells Kit + Studio
 * as the products of the visitor's active promo (`catalogue.ts`), so the
 * code is only ever a name for which products those are.
 */

export const PROMO_CODES = ['WELCOME40', 'FIRSTFREE'] as const

export type PromoCode = (typeof PROMO_CODES)[number]

/** The offer of a visitor whom no link has put in a test group. */
export const DEFAULT_PROMO_CODE: PromoCode = 'WELCOME40'

/** Accepts any string, any case, so a hand-typed link or a stored value can be validated. */
export function toPromoCode(value: string | null | undefined): PromoCode | undefined {
  const code = value?.trim().toUpperCase()
  return PROMO_CODES.find((known) => known === code)
}

/**
 * The test groups, as the neutral `?cx=` parameter names them: a short
 * tag rather than the code, so a link that puts a visitor in a group says nothing
 * about the offers.
 */
const GROUPS = new Map<string, PromoCode>([
  ['w40', 'WELCOME40'],
  ['ff', 'FIRSTFREE'],
])

/** What a link asks of the promo. */
export interface PromoLink {
  /** `?promo=WELCOME40`: apply this code, from the email that promised it. */
  apply?: PromoCode
  /** `?cx=ff`: offer this code, in this browser from now on. */
  group?: PromoCode
}

/** Read the promo parameters from a page's query string. Unknown values are ignored. */
export function readPromoLink(search: string): PromoLink {
  const params = new URLSearchParams(search)
  return {
    apply: toPromoCode(params.get('promo')),
    group: GROUPS.get(params.get('cx')?.trim().toLowerCase() ?? ''),
  }
}
